/**
 * Seed Modal State Management Hook
 *
 * Manages modal open/close states for all Seeds section modals:
 * - Comment modal (simple boolean state)
 * - Add Seed modal (simple boolean state)
 * - Confirmation modal (with status data)
 * - Tag modal (simple boolean state)
 * - Remove Tag modal (simple boolean state)
 * - Export modal (simple boolean state)
 *
 * Pattern: 90% transferable from Assets (simple state management)
 * Risk Level: Low
 * - Pure state management with no side effects
 * - useCallback optimization for stable function references
 * - Type-safe modal data handling
 */

import { useCallback, useState } from 'react';

import type { Seed, SeedStatus } from '@/types';

/**
 * Comment modal state interface
 */
interface CommentModalState {
  /** Whether the comment modal is open */
  isOpen: boolean;
  /** Open the comment modal */
  open: () => void;
  /** Close the comment modal */
  close: () => void;
}

/**
 * Add Seed modal state interface
 */
interface AddSeedModalState {
  /** Whether the add seed modal is open */
  isOpen: boolean;
  /** Open the add seed modal */
  open: () => void;
  /** Close the add seed modal */
  close: () => void;
}

/**
 * Confirmation modal data interface
 */
interface ConfirmationData {
  /** Status to confirm */
  status: SeedStatus;
  /** Seeds to update with the status */
  data: Seed[];
}

/**
 * Confirmation modal state interface
 */
interface ConfirmationModalState {
  /** Confirmation data (null when closed) */
  data: ConfirmationData | null;
  /** Open the confirmation modal with status and seeds */
  open: (status: SeedStatus, seeds: Seed[]) => void;
  /** Close the confirmation modal */
  close: () => void;
}

/**
 * Tag modal state interface
 */
interface TagModalState {
  /** Whether the tag modal is open */
  isOpen: boolean;
  /** Open the tag modal */
  open: () => void;
  /** Close the tag modal */
  close: () => void;
}

/**
 * Remove Tag modal state interface
 */
interface RemoveTagModalState {
  /** Whether the remove tag modal is open */
  isOpen: boolean;
  /** Open the remove tag modal */
  open: () => void;
  /** Close the remove tag modal */
  close: () => void;
}

/**
 * Export modal state interface
 */
interface ExportModalState {
  /** Whether the export modal is open */
  isOpen: boolean;
  /** Open the export modal */
  open: () => void;
  /** Close the export modal */
  close: () => void;
}

/**
 * Import modal state interface
 */
interface ImportModalState {
  /** Whether the import modal is open */
  isOpen: boolean;
  /** Open the import modal */
  open: () => void;
  /** Close the import modal */
  close: () => void;
}

/**
 * Return type for useSeedModal hook
 */
export interface SeedModalState {
  /** Comment modal state */
  comment: CommentModalState;
  /** Add seed modal state */
  addSeed: AddSeedModalState;
  /** Confirmation modal state */
  confirmation: ConfirmationModalState;
  /** Tag modal state */
  tag: TagModalState;
  /** Remove tag modal state */
  removeTag: RemoveTagModalState;
  /** Export modal state */
  export: ExportModalState;
  /** Import modal state */
  import: ImportModalState;
}

/**
 * Hook to manage all modal states in the Seeds section
 *
 * Provides organized, type-safe interface for modal operations:
 * - Comment modal: Simple open/close operations
 * - Add Seed modal: Simple open/close operations
 * - Confirmation modal: Open with data, close operations
 *
 * All functions are memoized with useCallback for optimal performance.
 *
 * @returns Object containing all modal states and operations
 *
 * @example
 * ```tsx
 * const modalState = useSeedModal();
 *
 * // Open modals
 * <Button onClick={modalState.addSeed.open}>Add Seed</Button>
 * <Button onClick={modalState.comment.open}>Add Comment</Button>
 * <Button onClick={() => modalState.confirmation.open(SeedStatus.Accept, seeds)}>
 *   Confirm Status
 * </Button>
 *
 * // Use in modal components
 * <CommentModal
 *   open={modalState.comment.isOpen}
 *   onClose={modalState.comment.close}
 * />
 * <AddSeedByTypeModal
 *   isOpen={modalState.addSeed.isOpen}
 *   onClose={modalState.addSeed.close}
 * />
 * <Modal
 *   open={!!modalState.confirmation.data}
 *   onClose={modalState.confirmation.close}
 * >
 *   {modalState.confirmation.data && (
 *     <div>Confirm {modalState.confirmation.data.status}</div>
 *   )}
 * </Modal>
 * ```
 */
export const useSeedModal = (): SeedModalState => {
  // Comment modal state
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);

  // Add seed modal state
  const [isAddSeedModalOpen, setIsAddSeedModalOpen] = useState(false);

  // Confirmation modal state with data
  const [confirmStatus, setConfirmStatus] = useState<ConfirmationData | null>(
    null
  );

  // Tag modal state
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);

  // Remove tag modal state
  const [isRemoveTagModalOpen, setIsRemoveTagModalOpen] = useState(false);

  // Export modal state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  // Import modal state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // Organized return object with nested modal states
  return {
    comment: {
      isOpen: isCommentModalOpen,
      open: useCallback(() => setIsCommentModalOpen(true), []),
      close: useCallback(() => setIsCommentModalOpen(false), []),
    },
    addSeed: {
      isOpen: isAddSeedModalOpen,
      open: useCallback(() => setIsAddSeedModalOpen(true), []),
      close: useCallback(() => setIsAddSeedModalOpen(false), []),
    },
    confirmation: {
      data: confirmStatus,
      open: useCallback(
        (status: string | SeedStatus, seeds: Seed[]) =>
          setConfirmStatus({ status: status as SeedStatus, data: seeds }),
        []
      ),
      close: useCallback(() => setConfirmStatus(null), []),
    },
    tag: {
      isOpen: isTagModalOpen,
      open: useCallback(() => setIsTagModalOpen(true), []),
      close: useCallback(() => setIsTagModalOpen(false), []),
    },
    removeTag: {
      isOpen: isRemoveTagModalOpen,
      open: useCallback(() => setIsRemoveTagModalOpen(true), []),
      close: useCallback(() => setIsRemoveTagModalOpen(false), []),
    },
    export: {
      isOpen: isExportModalOpen,
      open: useCallback(() => setIsExportModalOpen(true), []),
      close: useCallback(() => setIsExportModalOpen(false), []),
    },
    import: {
      isOpen: isImportModalOpen,
      open: useCallback(() => setIsImportModalOpen(true), []),
      close: useCallback(() => setIsImportModalOpen(false), []),
    },
  };
};
